/**
 * Hook: useFeatureFlag
 *
 * Lee flags de `appConfig/features` en Firestore para activar/desactivar
 * funcionalidades sin necesidad de un nuevo deploy. Cachea con React Query
 * y se refresca cada 5 minutos.
 *
 * Uso:
 *   const memberRolesEnabled = useFeatureFlag("memberRolesEnabled");
 *   if (memberRolesEnabled) { ... }
 *
 * Convenciones:
 * - Documento único `appConfig/features` cuyo shape es
 *   `{ memberRolesEnabled: true, someOtherFlag: false, ... }`.
 * - Si el documento no existe o falta el flag, se devuelve el `defaultValue`.
 * - Sólo admins de asociación/unión pueden editarlo (Firestore rules).
 */
import { useQuery } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

type FeatureFlagName = "memberRolesEnabled" | (string & {});

async function fetchFeatures(): Promise<Record<string, unknown>> {
  const snap = await getDoc(doc(db, "appConfig", "features"));
  if (!snap.exists()) return {};
  return snap.data() as Record<string, unknown>;
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ["appConfig", "features"],
    queryFn: fetchFeatures,
    staleTime: 5 * 60_000,
  });
}

export function useFeatureFlag(
  name: FeatureFlagName,
  defaultValue = false,
): boolean {
  const { data } = useFeatureFlags();
  if (!data) return defaultValue;
  const v = data[name];
  return typeof v === "boolean" ? v : defaultValue;
}
